<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'next/switch/Switch.vue';
import WorkingSchedulesTable from './WorkingSchedulesTable.vue';
import ProvetAPI from 'dashboard/api/provet';

const { t } = useI18n();
const isSyncing = ref(false);
const isSyncingCcMembership = ref(false);
const aiConversationHelperEnabled = ref(false);
const reviewRequestsEnabled = ref(false);
const isUpdatingAiHelper = ref(false);
const isUpdatingReviewRequests = ref(false);
const isSendingReviewRequests = ref(false);
const reviewRunDate = ref(
  new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' })
);
const reviewRunFrom = ref('07:00');
const reviewRunUntil = ref('14:00');
const isLoadingSettings = ref(true);
const clientId = ref(null);
const isTesting = ref(false);
const testDate = ref('');
const testTime = ref('');
const testResult = ref(null);

const handleSync = async () => {
  isSyncing.value = true;
  try {
    const response = await ProvetAPI.sync();
    if (response.data.success) {
      useAlert(t('PROVET_SETTINGS.SYNC.SUCCESS'));
    } else {
      useAlert(t('PROVET_SETTINGS.SYNC.ERROR'), 'error');
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('PROVET_SETTINGS.SYNC.ERROR');
    useAlert(errorMessage, 'error');
  } finally {
    isSyncing.value = false;
  }
};

const handleSyncCcMembership = async () => {
  isSyncingCcMembership.value = true;
  try {
    const response = await ProvetAPI.syncCcMembership();
    if (response.data.success) {
      useAlert(t('PROVET_SETTINGS.SYNC_CC_MEMBERSHIP.SUCCESS'));
    } else {
      useAlert(t('PROVET_SETTINGS.SYNC_CC_MEMBERSHIP.ERROR'), 'error');
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('PROVET_SETTINGS.SYNC_CC_MEMBERSHIP.ERROR');
    useAlert(errorMessage, 'error');
  } finally {
    isSyncingCcMembership.value = false;
  }
};

const isClientIdValid = computed(() => Number(clientId.value) > 0);
const canRunReviewRequests = computed(
  () =>
    Boolean(reviewRunFrom.value) &&
    Boolean(reviewRunUntil.value) &&
    reviewRunFrom.value < reviewRunUntil.value
);

const loadSettings = async () => {
  isLoadingSettings.value = true;
  try {
    const response = await ProvetAPI.getSettings();
    aiConversationHelperEnabled.value = !!response.data.ai_conversation_helper;
    reviewRequestsEnabled.value = !!response.data.review_requests_enabled;
  } catch (error) {
    useAlert(t('PROVET_SETTINGS.AI_CONVERSATION_HELPER.LOAD_ERROR'), 'error');
  } finally {
    isLoadingSettings.value = false;
  }
};

const handleToggleAiConversationHelper = async () => {
  const nextValue = aiConversationHelperEnabled.value;
  isUpdatingAiHelper.value = true;
  try {
    const response = await ProvetAPI.updateSettings({
      aiConversationHelper: nextValue,
    });
    aiConversationHelperEnabled.value = !!response.data.ai_conversation_helper;
    useAlert(t('PROVET_SETTINGS.AI_CONVERSATION_HELPER.SUCCESS'));
  } catch (error) {
    aiConversationHelperEnabled.value = !nextValue;
    const errorMessage =
      error?.response?.data?.error ||
      t('PROVET_SETTINGS.AI_CONVERSATION_HELPER.ERROR');
    useAlert(errorMessage, 'error');
  } finally {
    isUpdatingAiHelper.value = false;
  }
};

const handleToggleReviewRequests = async () => {
  const nextValue = reviewRequestsEnabled.value;
  isUpdatingReviewRequests.value = true;
  try {
    const response = await ProvetAPI.updateSettings({
      reviewRequestsEnabled: nextValue,
    });
    reviewRequestsEnabled.value = !!response.data.review_requests_enabled;
    useAlert(t('PROVET_SETTINGS.REVIEW_REQUESTS.SUCCESS'));
  } catch (error) {
    reviewRequestsEnabled.value = !nextValue;
    const errorMessage =
      error?.response?.data?.error ||
      t('PROVET_SETTINGS.REVIEW_REQUESTS.ERROR');
    useAlert(errorMessage, 'error');
  } finally {
    isUpdatingReviewRequests.value = false;
  }
};

const handleRunReviewRequests = async () => {
  if (!canRunReviewRequests.value || isSendingReviewRequests.value) return;

  isSendingReviewRequests.value = true;
  try {
    await ProvetAPI.sendReviewRequests({
      date: reviewRunDate.value,
      startedFrom: reviewRunFrom.value,
      startedUntil: reviewRunUntil.value,
    });
    useAlert(t('PROVET_SETTINGS.REVIEW_REQUESTS.RUN.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('PROVET_SETTINGS.REVIEW_REQUESTS.RUN.ERROR');
    useAlert(errorMessage, 'error');
  } finally {
    isSendingReviewRequests.value = false;
  }
};

onMounted(loadSettings);

const handleSyncClientById = async () => {
  if (!isClientIdValid.value) {
    return;
  }

  isSyncing.value = true;
  try {
    const response = await ProvetAPI.sync({ clientId: Number(clientId.value) });
    if (response.data.success) {
      useAlert(t('PROVET_SETTINGS.SYNC.SUCCESS'));
    } else {
      useAlert(t('PROVET_SETTINGS.SYNC.ERROR'), 'error');
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('PROVET_SETTINGS.SYNC.ERROR');
    useAlert(errorMessage, 'error');
  } finally {
    isSyncing.value = false;
  }
};

const handleTestSchedule = async () => {
  isTesting.value = true;
  testResult.value = null;
  try {
    const response = await ProvetAPI.testSchedule(testDate.value, testTime.value);
    if (response.data?.payload) {
      testResult.value = {
        out_of_hours: response.data.payload.out_of_hours,
        autoresponse: response.data.payload.autoresponse,
      };
      console.log(testResult.value);
    } else {
      useAlert(t('PROVET_SETTINGS.TEST_SCHEDULE.ERROR'), 'error');
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('PROVET_SETTINGS.TEST_SCHEDULE.ERROR');
    useAlert(errorMessage, 'error');
  } finally {
    isTesting.value = false;
  }
};
</script>

<template>
  <SettingsLayout class="max-w-4xl mx-auto">
    <template #header>
      <BaseSettingsHeader :title="$t('PROVET_SETTINGS.TITLE')" :description="$t('PROVET_SETTINGS.DESCRIPTION')" />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <div class="p-4 bg-n-solid-3 rounded-lg border border-n-weak">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h3 class="text-base font-semibold mb-2">
                {{ $t('PROVET_SETTINGS.AI_CONVERSATION_HELPER.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ $t('PROVET_SETTINGS.AI_CONVERSATION_HELPER.DESCRIPTION') }}
              </p>
            </div>
            <Switch
              v-model="aiConversationHelperEnabled"
              :disabled="isUpdatingAiHelper || isLoadingSettings"
              @change="handleToggleAiConversationHelper"
            />
          </div>
        </div>

        <div class="p-4 bg-n-solid-3 rounded-lg border border-n-weak">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h3 class="text-base font-semibold mb-2">
                {{ $t('PROVET_SETTINGS.REVIEW_REQUESTS.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ $t('PROVET_SETTINGS.REVIEW_REQUESTS.DESCRIPTION') }}
              </p>
            </div>
            <Switch
              v-model="reviewRequestsEnabled"
              :disabled="isUpdatingReviewRequests || isLoadingSettings"
              @change="handleToggleReviewRequests"
            />
          </div>
          <div class="mt-4 flex flex-col gap-3">
            <woot-input
              v-model="reviewRunDate"
              type="date"
              class="w-full"
              :label="$t('PROVET_SETTINGS.REVIEW_REQUESTS.RUN.DATE')"
            />
            <div class="flex gap-3">
              <woot-input
                v-model="reviewRunFrom"
                type="time"
                class="w-full"
                :label="$t('PROVET_SETTINGS.REVIEW_REQUESTS.RUN.FROM')"
              />
              <woot-input
                v-model="reviewRunUntil"
                type="time"
                class="w-full"
                :label="$t('PROVET_SETTINGS.REVIEW_REQUESTS.RUN.UNTIL')"
              />
            </div>
            <Button
              :label="$t('PROVET_SETTINGS.REVIEW_REQUESTS.RUN.BUTTON')"
              :is-loading="isSendingReviewRequests"
              :disabled="!canRunReviewRequests"
              @click="handleRunReviewRequests"
            />
          </div>
        </div>

        <div class="p-4 bg-n-solid-3 rounded-lg border border-n-weak">
          <h3 class="text-base font-semibold mb-2">
            {{ $t('PROVET_SETTINGS.SYNC.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-4">
            {{ $t('PROVET_SETTINGS.SYNC.DESCRIPTION') }}
          </p>
          <Button :label="$t('PROVET_SETTINGS.SYNC.BUTTON')" :is-loading="isSyncing" @click="handleSync" />
          <div class="mt-4 flex flex-col gap-3">
            <div class="w-full">
              <woot-input v-model.number="clientId" type="number" min="1" class="w-full"
                placeholder="Enter client ID" />
            </div>
            <div class="w-full">
              <Button label="Sync client by ID" class="w-full" :is-loading="isSyncing" :disabled="!isClientIdValid"
                @click="handleSyncClientById" />
            </div>
          </div>
        </div>

        <div class="p-4 bg-n-solid-3 rounded-lg border border-n-weak">
          <h3 class="text-base font-semibold mb-2">
            {{ $t('PROVET_SETTINGS.SYNC_CC_MEMBERSHIP.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-4">
            {{ $t('PROVET_SETTINGS.SYNC_CC_MEMBERSHIP.DESCRIPTION') }}
          </p>
          <Button
            :label="$t('PROVET_SETTINGS.SYNC_CC_MEMBERSHIP.BUTTON')"
            :is-loading="isSyncingCcMembership"
            @click="handleSyncCcMembership"
          />
        </div>

        <div class="p-4 bg-n-solid-3 rounded-lg border border-n-weak">
          <h3 class="text-base font-semibold mb-2">
            {{ $t('PROVET_SETTINGS.TEST_SCHEDULE.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-4">
            {{ $t('PROVET_SETTINGS.TEST_SCHEDULE.DESCRIPTION') }}
          </p>
          <div class="flex flex-col gap-4">
            <woot-input v-model="testDate" :formatter="date => date ? new Date(date).toLocaleDateString('en-GB') : ''"
              type="date" class="w-full" :label="$t('PROVET_SETTINGS.TEST_SCHEDULE.DATE.LABEL')"
              :placeholder="$t('PROVET_SETTINGS.TEST_SCHEDULE.DATE.PLACEHOLDER')" />
            <woot-input v-model="testTime" type="time" class="w-full"
              :label="$t('PROVET_SETTINGS.TEST_SCHEDULE.TIME.LABEL')"
              :placeholder="$t('PROVET_SETTINGS.TEST_SCHEDULE.TIME.PLACEHOLDER')" />
            <Button :label="$t('PROVET_SETTINGS.TEST_SCHEDULE.BUTTON')" :is-loading="isTesting"
              @click="handleTestSchedule" />
            <div v-if="testResult !== null" class="p-3 rounded-lg" :class="testResult.out_of_hours
              ? 'bg-ruby-3 border border-ruby-6'
              : 'bg-green-3 border border-green-6'
              ">
              <p class="text-sm font-medium" :class="testResult.out_of_hours ? 'text-ruby-11' : 'text-green-11'">
                {{
                  testResult.out_of_hours
                    ? $t('PROVET_SETTINGS.TEST_SCHEDULE.RESULT.OUT_OF_HOURS')
                    : $t('PROVET_SETTINGS.TEST_SCHEDULE.RESULT.IN_HOURS')
                }}
              </p>
              <p class="text-sm mt-2 whitespace-pre-line text-n-slate-11">
                {{ testResult.autoresponse }}
              </p>
            </div>
          </div>
        </div>

        <div class="p-4 bg-n-solid-3 rounded-lg border border-n-weak">
          <WorkingSchedulesTable />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
